/**
 * Panel de Agenda del Evento.
 *
 * Tres modos de visualización (mismo contenido):
 *   - Admin (organizador): puede editar/agregar/eliminar días y bloques.
 *   - Director / miembro: solo lectura.
 *
 * Modelo: la agenda se guarda dentro del doc del evento (`event.agenda`).
 *  - `version` se incrementa en cada guardado → permite mostrar banner
 *    "Agenda actualizada" comparando contra `localStorage` por usuario+evento.
 *  - Indicador "Sucediendo ahora" para el bloque activo según hora del cliente.
 */

import { useMemo, useRef, useState } from "react";
import { Timestamp } from "firebase/firestore";
import {
  Calendar,
  Edit3,
  Plus,
  Save,
  Trash2,
  AlertCircle,
  X,
  Upload,
  Download,
  Share2,
} from "lucide-react";
import { Button, Badge, Card, CardContent } from "@/components/ui";
import AgendaTimeline from "@/components/agenda/AgendaTimeline";
import { eventService } from "@/lib/services/events";
import { useAuthStore } from "@/stores/authStore";
import { toast } from "react-hot-toast";
import type {
  Event,
  EventAgenda,
  EventAgendaBlock,
  EventAgendaDay,
} from "@/types";

interface Props {
  event: Event;
  /** Solo el organizador del evento ve los controles de edición. */
  canEdit: boolean;
}

function makeId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function emptyBlock(): EventAgendaBlock {
  return {
    id: makeId(),
    startTime: "08:00",
    endTime: "09:00",
    title: "",
  };
}

/** Devuelve "YYYY-MM-DD" del Date local. */
function nowYMD(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function lsKey(eventId: string, uid: string | undefined): string {
  return `agendaSeenVersion:${eventId}:${uid || "anon"}`;
}

// ===== CSV =====

/** Parsea una línea CSV soportando comillas dobles y escapes "". */
function parseCsvLine(line: string, sep: string): string[] {
  const out: string[] = [];
  let cur = "";
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (inQ) {
      if (c === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQ = false;
        }
      } else {
        cur += c;
      }
    } else {
      if (c === '"') inQ = true;
      else if (c === sep) {
        out.push(cur);
        cur = "";
      } else cur += c;
    }
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

interface CsvParseResult {
  days: EventAgendaDay[];
  errors: string[];
  rows: number;
}

/** Convierte CSV a EventAgendaDay[]. Acepta separador , o ;. */
function parseAgendaCsv(text: string): CsvParseResult {
  const errors: string[] = [];
  const clean = text.replace(/^\uFEFF/, "").replace(/\r\n?/g, "\n").trim();
  if (!clean) return { days: [], errors: ["El archivo está vacío"], rows: 0 };

  const firstLine = clean.split("\n")[0];
  const sep = firstLine.includes(";") && !firstLine.includes(",") ? ";" : ",";

  const lines = clean.split("\n").filter((l) => l.trim().length > 0);
  const header = parseCsvLine(lines[0], sep).map((h) => h.toLowerCase());

  const required = ["date", "starttime", "endtime", "title"];
  for (const r of required) {
    if (!header.includes(r)) {
      return {
        days: [],
        errors: [
          `Falta columna obligatoria "${r}". Encabezado esperado: date,startTime,endTime,title,location,description,dayCoordinator`,
        ],
        rows: 0,
      };
    }
  }

  const idx = (name: string) => header.indexOf(name.toLowerCase());
  const iDate = idx("date");
  const iStart = idx("starttime");
  const iEnd = idx("endtime");
  const iTitle = idx("title");
  const iLoc = idx("location");
  const iDesc = idx("description");
  const iCoord = idx("daycoordinator");

  const dayMap = new Map<string, EventAgendaDay>();
  let rows = 0;

  for (let li = 1; li < lines.length; li++) {
    const cols = parseCsvLine(lines[li], sep);
    const date = cols[iDate]?.trim();
    const startTime = cols[iStart]?.trim();
    const endTime = cols[iEnd]?.trim();
    const title = cols[iTitle]?.trim();

    if (!date && !startTime && !title) continue; // fila vacía
    rows++;

    const ln = li + 1;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date || "")) {
      errors.push(`Línea ${ln}: fecha inválida "${date}" (use YYYY-MM-DD)`);
      continue;
    }
    if (!/^\d{2}:\d{2}$/.test(startTime || "")) {
      errors.push(`Línea ${ln}: hora inicio inválida "${startTime}" (HH:MM)`);
      continue;
    }
    if (!/^\d{2}:\d{2}$/.test(endTime || "")) {
      errors.push(`Línea ${ln}: hora fin inválida "${endTime}" (HH:MM)`);
      continue;
    }
    if (startTime >= endTime) {
      errors.push(`Línea ${ln}: la hora fin debe ser mayor que la inicio`);
      continue;
    }
    if (!title) {
      errors.push(`Línea ${ln}: el título es obligatorio`);
      continue;
    }

    let day = dayMap.get(date);
    if (!day) {
      day = { id: makeId(), date, dayCoordinator: "", blocks: [] };
      dayMap.set(date, day);
    }
    const coord = iCoord >= 0 ? cols[iCoord]?.trim() : "";
    if (coord && !day.dayCoordinator) day.dayCoordinator = coord;

    const block: EventAgendaBlock = {
      id: makeId(),
      startTime: startTime!,
      endTime: endTime!,
      title,
    };
    const loc = iLoc >= 0 ? cols[iLoc]?.trim() : "";
    const desc = iDesc >= 0 ? cols[iDesc]?.trim() : "";
    if (loc) block.location = loc;
    if (desc) block.description = desc;
    day.blocks.push(block);
  }

  return { days: Array.from(dayMap.values()), errors, rows };
}

/** Serializa la agenda actual a CSV (para exportar/plantilla). */
function agendaToCsv(agenda: EventAgenda): string {
  const esc = (v: string | undefined) => {
    const s = v ?? "";
    if (/[",;\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const header =
    "date,startTime,endTime,title,location,description,dayCoordinator";
  const lines = [header];
  for (const d of agenda.days) {
    if (d.blocks.length === 0) {
      lines.push(
        `${d.date},,,,,${esc("")},${esc(d.dayCoordinator)}`,
      );
      continue;
    }
    for (const b of d.blocks) {
      lines.push(
        [
          d.date,
          b.startTime,
          b.endTime,
          esc(b.title),
          esc(b.location),
          esc(b.description),
          esc(d.dayCoordinator),
        ].join(","),
      );
    }
  }
  return lines.join("\n");
}

const CSV_TEMPLATE = `date,startTime,endTime,title,location,description,dayCoordinator
2026-07-13,08:00,09:00,Llegada y registro,Plazoleta,Recepción de clubes,Pr. Gustavo Ibarra
2026-07-13,09:00,10:00,Apertura oficial,Auditorio,,Pr. Gustavo Ibarra
2026-07-14,07:00,08:00,Devocional,Capilla,,Pr. Gustavo Ibarra
`;

export default function EventAgendaPanel({ event, canEdit }: Props) {
  const { user } = useAuthStore();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<EventAgenda | null>(null);
  const [saving, setSaving] = useState(false);

  const agenda: EventAgenda = useMemo(
    () => event.agenda ?? { days: [], version: 0 },
    [event.agenda],
  );

  // Banner "Agenda actualizada" — compara version vista contra actual.
  const seenKey = lsKey(event.id, user?.uid);
  const [seenVersion, setSeenVersion] = useState<number>(() => {
    if (typeof window === "undefined") return agenda.version;
    const raw = window.localStorage.getItem(seenKey);
    return raw ? Number(raw) || 0 : 0;
  });
  const isUpdated = !editing && agenda.version > 0 && seenVersion < agenda.version;

  const acknowledgeUpdate = () => {
    try {
      window.localStorage.setItem(seenKey, String(agenda.version));
    } catch {
      /* ignore */
    }
    setSeenVersion(agenda.version);
  };

  const startEdit = () => {
    setDraft({
      days: agenda.days.map((d) => ({
        ...d,
        blocks: d.blocks.map((b) => ({ ...b })),
      })),
      version: agenda.version,
    });
    setEditing(true);
  };

  const cancelEdit = () => {
    setDraft(null);
    setEditing(false);
  };

  const addDay = () => {
    if (!draft) return;
    const newDay: EventAgendaDay = {
      id: makeId(),
      date: nowYMD(new Date()),
      dayCoordinator: "",
      blocks: [],
    };
    setDraft({ ...draft, days: [...draft.days, newDay] });
  };

  const removeDay = (dayId: string) => {
    if (!draft) return;
    if (!window.confirm("¿Eliminar este día y todos sus bloques?")) return;
    setDraft({ ...draft, days: draft.days.filter((d) => d.id !== dayId) });
  };

  const updateDay = (dayId: string, patch: Partial<EventAgendaDay>) => {
    if (!draft) return;
    setDraft({
      ...draft,
      days: draft.days.map((d) => (d.id === dayId ? { ...d, ...patch } : d)),
    });
  };

  const addBlock = (dayId: string) => {
    if (!draft) return;
    setDraft({
      ...draft,
      days: draft.days.map((d) =>
        d.id === dayId ? { ...d, blocks: [...d.blocks, emptyBlock()] } : d,
      ),
    });
  };

  const updateBlock = (
    dayId: string,
    blockId: string,
    patch: Partial<EventAgendaBlock>,
  ) => {
    if (!draft) return;
    setDraft({
      ...draft,
      days: draft.days.map((d) =>
        d.id !== dayId
          ? d
          : {
              ...d,
              blocks: d.blocks.map((b) =>
                b.id === blockId ? { ...b, ...patch } : b,
              ),
            },
      ),
    });
  };

  const removeBlock = (dayId: string, blockId: string) => {
    if (!draft) return;
    setDraft({
      ...draft,
      days: draft.days.map((d) =>
        d.id !== dayId
          ? d
          : { ...d, blocks: d.blocks.filter((b) => b.id !== blockId) },
      ),
    });
  };

  const validate = (a: EventAgenda): string | null => {
    for (const d of a.days) {
      if (!d.date) return "Cada día debe tener fecha.";
      for (const b of d.blocks) {
        if (!b.title.trim()) return "Cada bloque debe tener un título.";
        if (!b.startTime || !b.endTime)
          return `Bloque "${b.title}": faltan horas.`;
        if (b.startTime >= b.endTime)
          return `Bloque "${b.title}": la hora fin debe ser mayor que la inicio.`;
      }
    }
    return null;
  };

  const save = async () => {
    if (!draft) return;
    const err = validate(draft);
    if (err) {
      toast.error(err);
      return;
    }
    // Ordenar días por fecha y bloques por hora antes de guardar.
    const sorted: EventAgenda = {
      version: (agenda.version || 0) + 1,
      updatedAt: Timestamp.now(),
      updatedBy: user?.uid,
      updatedByName: user?.displayName || user?.email || undefined,
      days: [...draft.days]
        .sort((a, b) => a.date.localeCompare(b.date))
        .map((d) => ({
          ...d,
          blocks: [...d.blocks].sort((x, y) =>
            x.startTime.localeCompare(y.startTime),
          ),
        })),
    };
    setSaving(true);
    try {
      await eventService.update(event.id, { agenda: sorted });
      toast.success("Agenda guardada");
      setEditing(false);
      setDraft(null);
      // Marcar como vista para no auto-banner al organizador que acaba de editar.
      try {
        window.localStorage.setItem(seenKey, String(sorted.version));
      } catch {
        /* ignore */
      }
      setSeenVersion(sorted.version);
    } catch (e) {
      console.error(e);
      toast.error("No se pudo guardar la agenda");
    } finally {
      setSaving(false);
    }
  };

  // ===== CSV import / export =====
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleCsvFile = async (file: File) => {
    if (!draft) return;
    try {
      const text = await file.text();
      const result = parseAgendaCsv(text);
      if (result.errors.length > 0) {
        toast.error(result.errors.slice(0, 3).join("\n"), { duration: 6000 });
        return;
      }
      if (result.days.length === 0) {
        toast.error("No se encontraron filas válidas en el CSV");
        return;
      }
      const totalBlocks = result.days.reduce(
        (n, d) => n + d.blocks.length,
        0,
      );
      const replace = window.confirm(
        `Se importarán ${result.days.length} día(s) y ${totalBlocks} bloque(s).\n\n` +
          `¿REEMPLAZAR la agenda actual?\n` +
          `• Aceptar = reemplazar todo\n` +
          `• Cancelar = agregar a lo existente`,
      );
      if (replace) {
        setDraft({ ...draft, days: result.days });
        toast.success(`Agenda reemplazada (${totalBlocks} bloques)`);
      } else {
        // Merge por fecha: si ya existe el día, añade sus bloques.
        const merged = new Map<string, EventAgendaDay>();
        for (const d of draft.days) merged.set(d.date, d);
        for (const d of result.days) {
          const existing = merged.get(d.date);
          if (existing) {
            existing.blocks.push(...d.blocks);
            if (!existing.dayCoordinator && d.dayCoordinator)
              existing.dayCoordinator = d.dayCoordinator;
          } else {
            merged.set(d.date, d);
          }
        }
        setDraft({ ...draft, days: Array.from(merged.values()) });
        toast.success(`Se agregaron ${totalBlocks} bloques`);
      }
    } catch (e) {
      console.error(e);
      toast.error("No se pudo leer el archivo CSV");
    }
  };

  const downloadCsvTemplate = () => {
    const content =
      agenda.days.length > 0 ? agendaToCsv(agenda) : CSV_TEMPLATE;
    const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download =
      agenda.days.length > 0
        ? `agenda-${event.id}.csv`
        : "plantilla-agenda.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  // ===== Render =====

  // Banner de actualización (solo lectura).
  const banner = isUpdated && (
    <div className="rounded-lg border border-blue-300 bg-blue-50 p-3 flex items-start gap-3">
      <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
      <div className="flex-1 text-sm text-blue-900">
        <p className="font-semibold">La agenda fue actualizada</p>
        <p className="text-xs">
          {agenda.updatedByName
            ? `${agenda.updatedByName} hizo cambios recientemente.`
            : "Hay cambios recientes en la agenda."}
        </p>
      </div>
      <button
        onClick={acknowledgeUpdate}
        className="text-blue-700 hover:bg-blue-100 rounded p-1"
        title="Marcar como visto"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );

  // ===== Modo edición =====
  if (editing && draft) {
    return (
      <div className="space-y-4">
        <Card className="border-0 shadow-lg">
          <CardContent className="p-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Edit3 className="w-5 h-5 text-purple-600" />
              <h3 className="font-semibold text-gray-900">Editando agenda</h3>
              <Badge className="bg-purple-100 text-purple-700">
                v{(agenda.version || 0) + 1}
              </Badge>
            </div>
            <div className="flex gap-2 flex-wrap">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleCsvFile(f);
                  e.target.value = "";
                }}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={downloadCsvTemplate}
                disabled={saving}
                title="Descarga la plantilla o exporta la agenda actual"
              >
                <Download className="w-4 h-4 mr-1" />
                Plantilla
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={saving}
                title="Importar agenda desde CSV"
              >
                <Upload className="w-4 h-4 mr-1" />
                Importar CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={cancelEdit}
                disabled={saving}
              >
                Cancelar
              </Button>
              <Button size="sm" onClick={save} disabled={saving}>
                <Save className="w-4 h-4 mr-1" />
                {saving ? "Guardando..." : "Guardar"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {draft.days.length === 0 && (
          <div className="rounded-lg border-2 border-dashed border-gray-300 p-8 text-center text-gray-500">
            Aún no hay días. Agrega el primero abajo.
          </div>
        )}

        {draft.days.map((day) => (
          <Card key={day.id} className="border-0 shadow">
            <CardContent className="p-4 space-y-3">
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[200px]">
                  <label className="text-xs font-medium text-gray-600 mb-1 block">
                    Fecha
                  </label>
                  <input
                    type="date"
                    value={day.date}
                    onChange={(e) =>
                      updateDay(day.id, { date: e.target.value })
                    }
                    className="w-full rounded-lg border-2 border-gray-200 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none"
                  />
                </div>
                <div className="flex-1 min-w-[240px]">
                  <label className="text-xs font-medium text-gray-600 mb-1 block">
                    Coordinador(es) del día
                  </label>
                  <input
                    type="text"
                    value={day.dayCoordinator ?? ""}
                    placeholder="Pr. Gustavo Ibarra"
                    onChange={(e) =>
                      updateDay(day.id, { dayCoordinator: e.target.value })
                    }
                    className="w-full rounded-lg border-2 border-gray-200 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => removeDay(day.id)}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Eliminar día
                </Button>
              </div>

              <div className="space-y-2">
                {day.blocks.map((b) => (
                  <div
                    key={b.id}
                    className="grid grid-cols-12 gap-2 items-start bg-gray-50 rounded-lg p-2"
                  >
                    <input
                      type="time"
                      value={b.startTime}
                      onChange={(e) =>
                        updateBlock(day.id, b.id, { startTime: e.target.value })
                      }
                      className="col-span-3 sm:col-span-2 rounded-md border border-gray-200 px-2 py-1.5 text-sm"
                    />
                    <input
                      type="time"
                      value={b.endTime}
                      onChange={(e) =>
                        updateBlock(day.id, b.id, { endTime: e.target.value })
                      }
                      className="col-span-3 sm:col-span-2 rounded-md border border-gray-200 px-2 py-1.5 text-sm"
                    />
                    <input
                      type="text"
                      value={b.title}
                      placeholder="Título"
                      onChange={(e) =>
                        updateBlock(day.id, b.id, { title: e.target.value })
                      }
                      className="col-span-12 sm:col-span-5 rounded-md border border-gray-200 px-2 py-1.5 text-sm"
                    />
                    <input
                      type="text"
                      value={b.location ?? ""}
                      placeholder="Lugar (opcional)"
                      onChange={(e) =>
                        updateBlock(day.id, b.id, { location: e.target.value })
                      }
                      className="col-span-10 sm:col-span-2 rounded-md border border-gray-200 px-2 py-1.5 text-sm"
                    />
                    <button
                      onClick={() => removeBlock(day.id, b.id)}
                      className="col-span-2 sm:col-span-1 flex items-center justify-center text-red-600 hover:bg-red-50 rounded-md"
                      title="Eliminar bloque"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <input
                      type="text"
                      value={b.description ?? ""}
                      placeholder="Descripción / nota (opcional)"
                      onChange={(e) =>
                        updateBlock(day.id, b.id, {
                          description: e.target.value,
                        })
                      }
                      className="col-span-12 rounded-md border border-gray-200 px-2 py-1.5 text-sm"
                    />
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => addBlock(day.id)}
                  className="w-full"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Agregar bloque
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}

        <Button onClick={addDay} variant="outline" className="w-full">
          <Plus className="w-4 h-4 mr-1" />
          Agregar día
        </Button>
      </div>
    );
  }

  // ===== Modo lectura =====
  return (
    <div className="space-y-4">
      {banner}

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-purple-600" />
          <h3 className="font-semibold text-gray-900">Agenda del evento</h3>
          {agenda.version > 0 && (
            <Badge className="bg-gray-100 text-gray-600 text-xs">
              v{agenda.version}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            size="sm"
            variant="outline"
            onClick={async () => {
              const url = `${window.location.origin}/agenda/${event.id}`;
              try {
                await navigator.clipboard.writeText(url);
                toast.success("Link público copiado al portapapeles");
              } catch {
                window.prompt("Copia el link público:", url);
              }
            }}
            title="Copiar link público de la agenda (no requiere login)"
          >
            <Share2 className="w-4 h-4 mr-1" />
            Compartir agenda
          </Button>
          {canEdit && (
            <Button size="sm" variant="outline" onClick={startEdit}>
              <Edit3 className="w-4 h-4 mr-1" />
              {agenda.days.length === 0 ? "Crear agenda" : "Editar agenda"}
            </Button>
          )}
        </div>
      </div>

      {agenda.days.length === 0 ? (
        <Card className="border-0 shadow">
          <CardContent className="p-8 text-center text-gray-500">
            <Calendar className="w-10 h-10 mx-auto text-gray-300 mb-2" />
            <p className="font-medium">Aún no hay agenda publicada</p>
            <p className="text-xs mt-1">
              {canEdit
                ? "Crea la agenda para que clubes y miembros la puedan consultar."
                : "El organizador aún no ha publicado la agenda."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <AgendaTimeline days={agenda.days} />
      )}

      {agenda.updatedByName && agenda.updatedAt && (
        <p className="text-[10px] text-gray-400 text-right">
          Última actualización por {agenda.updatedByName}
        </p>
      )}
    </div>
  );
}
